import React from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

/**
 * Calculate max lines per breakpoint automatically
 * Current usage: calculating the min line height of StatusText Titles so that the content texts are horizontally aligned. Title heights adjust based on how many lines the text will take up. The min height is calculated base don the max line count of all children and then synchronized to all children (e.g. 2 side by side titles will have the same height even if they have diff line counts)
 * Adapts to screen size and title changes
 */
export function useMaxLines(breakpoints = ['xs','sm','md']) {
  const theme = useTheme();

  // Track which breakpoint is active
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const currentBreakpoint = isXs ? 'xs' : isSm ? 'sm' : 'md';

  const [linesByChild, setLinesByChild] = React.useState({});

  /**
   * Each child calls this to report its line count 
   * The hook assigns it to the current breakpoint automatically
   */
  const handleLineCount = React.useCallback((childId, lines) => {
    setLinesByChild(prev => ({
      ...prev,
      [childId]: { ...(prev[childId] || {}), [currentBreakpoint]: lines }
    }));
  }, [currentBreakpoint]);

  /**
   * Returns max lines for the current breakpoint
   */
  const getMaxLines = React.useCallback(() => {
    return Math.max(
      1,
      ...Object.values(linesByChild).map(child => child[currentBreakpoint] || 0)
    );
  }, [linesByChild, currentBreakpoint]);

  return { handleLineCount, getMaxLines, currentBreakpoint };
}
